/* eslint-disable sort-keys-fix/sort-keys-fix */
import React from 'react';
import { arrayOf, func, shape, string } from 'prop-types';
import * as R from 'ramda';

// Components
import { Box, ButtonBase, Typography, useMediaQuery } from '@mui/material';
import AdjustIcon from '@mui/icons-material/Adjust';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { styled, useTheme } from '@mui/material/styles';
import { formatTime } from 'feature/loading-management/utils/timeHelpers';

const ArrowStyles = theme => ({
  color: theme.palette.primary.dark,
  height: 18,
  position: 'absolute',
  right: -18,
  top: 0,
  width: 18,
});

const ArrowSmStyles = theme => ({
  color: theme.palette.primary.dark,
  height: 16,
  position: 'absolute',
  right: -15,
  top: 1,
  width: 16,
});

const IconWrapper = styled(Box)(() => ({
  display: 'flex',
  height: 10,
  marginRight: 2,
  marginTop: -2,
  position: 'relative',
  width: 10,
}));

const Label = styled(ButtonBase)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    justifySelf: 'start',
  },
  '&:last-child': {
    marginRight: 0,
  },
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  justifySelf: 'end',
  position: 'relative',
}));

const Live = styled(ButtonBase)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    justifySelf: 'start',
  },
  ...theme.typography.subtitle2,
  alignItems: 'center',
  color: theme.palette.text.secondary,
  display: 'flex',
  justifySelf: 'end',
  position: 'relative',
}));

const LiveIcon = styled(AdjustIcon)(() => ({
  height: '100%',
  width: '100%',
}));

const Location = styled(ButtonBase)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    width: 'initial',
  },
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  position: 'relative',
}));

const Pulse = styled(Box)(() => ({
  animation: '$pulse 1.5s ease-in-out',
  animationIterationCount: 'infinite',
  border: '2px solid rgba(38, 126, 77, 0.5)', // needs opacity - cannot reference theme
  borderRadius: '50%',
  height: 10,
  left: 'calc(50% - 5px)',
  position: 'absolute',
  top: 'calc(50% - 5px)',
  width: 10,
}));

const ListLabel = ({
  lastFetchTime,
  locations,
  setSortColumn,
  setSortDirection,
  sortColumn,
  sortDirection,
}) => {
  const theme = useTheme();

  // eslint-disable-next-line no-shadow
  const mdUp = useMediaQuery(theme => theme.breakpoints.up('md'), { noSsr: true });

  const times = locations.filter(l => !R.isEmpty(l.waitTimes) && !R.isNil(l.waitTimes[0]))[0]
    ?.waitTimes;

  const renderSort = (str, sm = false) => {
    let icon;
    if (str === sortColumn && sm) {
      sortDirection === 'dsc'
        ? (icon = <ArrowDownwardIcon sx={ArrowSmStyles(theme)} />)
        : (icon = <ArrowUpwardIcon sx={ArrowSmStyles(theme)} />);
    } else if (str === sortColumn && !sm) {
      sortDirection === 'dsc'
        ? (icon = <ArrowDownwardIcon sx={ArrowStyles(theme)} />)
        : (icon = <ArrowUpwardIcon sx={ArrowStyles(theme)} />);
    }
    return icon;
  };

  const handleOnClick = col => {
    sortColumn !== col && setSortColumn(col);
    if (sortColumn === col) {
      sortDirection === 'asc' ? setSortDirection('dsc') : setSortDirection('asc');
    } else {
      setSortDirection('dsc');
    }
  };

  const getKey = i => times && times[i].key;

  return (
    <Box
      sx={{
        // eslint-disable-next-line no-shadow
        borderBottom: theme => `1px solid ${theme.palette.graphic.light}`,
        backgroundColor: { xs: 'graphic.lighter', md: 'initial' },
        display: 'flex',
        flexDirection: 'column',
        marginTop: { xs: 0, md: 12 },
        pb: { xs: 1.5, md: 2.5 },
        pl: { xs: 2, md: 3 },
        pr: 2,
        pt: 2,
        width: '100%',
      }}
    >
      <Typography
        sx={{
          alignSelf: { xs: 'flex-start', md: 'flex-end' },
          color: 'text.disabled',
          mb: { xs: 0, md: 1 },
        }}
        variant="caption"
      >
        Updated {lastFetchTime} Central Time
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: { xs: 1, md: 'revert' },
        }}
      >
        {mdUp && (
          <Location onClick={() => handleOnClick('name')}>
            LOCATION
            {renderSort('name')}
          </Location>
        )}
        <Box
          sx={{
            display: { xs: 'grid', md: 'flex' },
            flex: { xs: '0 0 calc(100% - 12px)', md: '0 0 calc(100% - 182px)' },
            gridTemplateColumns: 'repeat(4, 1fr)',
            justifyContent: 'space-between',
            pr: { xs: 0, md: 0.5 },
          }}
        >
          <Live onClick={() => handleOnClick(getKey(0))}>
            <IconWrapper>
              <LiveIcon color="primary" />
              <Pulse />
            </IconWrapper>
            LIVE
            {renderSort(getKey(0), true)}
          </Live>
          <Label onClick={() => handleOnClick(getKey(1))}>
            {formatTime(30, mdUp)}
            {renderSort(getKey(1), true)}
          </Label>
          <Label onClick={() => handleOnClick(getKey(2))}>
            {formatTime(60, mdUp)}
            {renderSort(getKey(2), true)}
          </Label>
          <Label onClick={() => handleOnClick(getKey(3))}>
            {formatTime(90, mdUp)}
            {renderSort(getKey(3), true)}
          </Label>
        </Box>
      </Box>
    </Box>
  );
};

ListLabel.propTypes = {
  lastFetchTime: string.isRequired,
  locations: arrayOf(shape({})).isRequired,
  setSortColumn: func.isRequired,
  setSortDirection: func.isRequired,
  sortColumn: string.isRequired,
  sortDirection: string.isRequired,
};

export default ListLabel;
